import { faqs } from "data/questions";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  UserIcon,
  CalendarDaysIcon,
  HomeIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";

interface FAQItemProps {
  question: string;
  answer: string | JSX.Element;
  className?: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 mb-6 overflow-hidden ${className}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full p-6 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 group"
        aria-expanded={isOpen}
      >
        <span className="text-lg font-semibold text-gray-900 dark:text-white pr-4 group-hover:text-blue-600 transition-colors">
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="flex-shrink-0"
        >
          <ChevronDownIcon className="w-6 h-6 text-gray-500 group-hover:text-blue-500 transition-colors" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-gray-600 dark:text-gray-300 leading-relaxed border-t border-gray-100 dark:border-gray-700 pt-4">
              {typeof answer === "string" ? (
                <p>
                  {answer ||
                    "Cette question sera bientôt mise à jour avec une réponse détaillée. En attendant, n'hésitez pas à nous contacter directement."}
                </p>
              ) : (
                answer
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ModernHelpCenter: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("fq");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [allFAQs, setAllFAQs] = useState<any[]>([]);

  useEffect(() => {
    // Flatten all FAQs for search
    const flattened = Object.entries(faqs).flatMap(([category, questions]) =>
      questions.map((q) => ({ ...q, category }))
    );
    setAllFAQs(flattened);
  }, []);

  const categories = [
    {
      name: "Utilisateurs",
      prefix: "utilisateurs",
      icon: UserIcon,
      color: "from-blue-500 to-blue-600",
      description: "Questions sur l'utilisation de la plateforme",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
      iconColor: "text-blue-600",
    },
    {
      name: "Sécurité & Annulation",
      prefix: "sécurité",
      icon: ShieldCheckIcon,
      color: "from-green-500 to-green-600",
      description: "Sécurité et conditions d'annulation",
      bgColor: "bg-green-50 dark:bg-green-900/20",
      iconColor: "text-green-600",
    },
    {
      name: "Paiement & Tarifs",
      prefix: "paiement",
      icon: CreditCardIcon,
      color: "from-purple-500 to-purple-600",
      description: "Facturation et modes de paiement",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
      iconColor: "text-purple-600",
    },
    {
      name: "Hôte des espaces",
      prefix: "hôte",
      icon: HomeIcon,
      color: "from-orange-500 to-orange-600",
      description: "Gestion et mise en location d'espaces",
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
      iconColor: "text-orange-600",
    },
    {
      name: "Réservation",
      prefix: "réservation",
      icon: CalendarDaysIcon,
      color: "from-pink-500 to-pink-600",
      description: "Processus de réservation",
      bgColor: "bg-pink-50 dark:bg-pink-900/20",
      iconColor: "text-pink-600",
    },
    {
      name: "Prestataire",
      prefix: "prestataire",
      icon: UserIcon,
      color: "from-indigo-500 to-indigo-600",
      description: "Services et prestations",
      bgColor: "bg-indigo-50 dark:bg-indigo-900/20",
      iconColor: "text-indigo-600",
    },
  ];

  const getFilteredFAQs = () => {
    if (searchQuery.trim() === "") {
      return faqs[selectedCategory] || [];
    }

    if (selectedCategory === "fq") {
      // Search across all categories
      return allFAQs.filter(
        (faq: any) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );
    } else {
      // Search within selected category
      return (faqs[selectedCategory] || []).filter(
        (faq: any) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
  };

  const filteredFAQs = getFilteredFAQs();
  const selectedCategoryData = categories.find((cat) => cat.prefix === selectedCategory);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Centre d'aide - Event To Bee</title>
        <meta
          name="description"
          content="Trouvez rapidement des réponses à vos questions sur Event To Bee. FAQ complète et centre d'aide moderne."
        />
        <link rel="canonical" href="https://eventtobee.com/faq" />
      </Helmet>

      <BgGlassmorphism />

      {/* Hero Section */}
      <section className="relative py-20 text-center bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex justify-center items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 backdrop-blur-sm rounded-full">
                <QuestionMarkCircleIcon className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white">Centre d'aide</h1>
            </div>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Comment pouvons-nous vous aider aujourd'hui ? Trouvez rapidement des réponses à vos questions.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher dans la FAQ... (ex: comment réserver, paiement, annulation)"
                  className="w-full pl-12 pr-6 py-4 text-lg border-0 rounded-2xl focus:outline-none focus:ring-4 focus:ring-white/30 bg-white/95 backdrop-blur-sm shadow-2xl"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  <button className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                    <SparklesIcon className="w-5 h-5 inline mr-2" />
                    Rechercher
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories or Search Results */}
      {selectedCategory === "fq" ? (
        <>
          {/* Categories Grid */}
          <section className="py-16 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Choisissez une catégorie</h2>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Sélectionnez le sujet qui vous intéresse pour voir les questions fréquentes
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category, index) => {
                  const Icon = category.icon;
                  return (
                    <motion.div
                      key={category.prefix}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                      whileHover={{ y: -8, scale: 1.02 }}
                      className="cursor-pointer group"
                      onClick={() => setSelectedCategory(category.prefix)}
                    >
                      <div
                        className={`p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 ${category.bgColor} border border-gray-200 dark:border-gray-700`}
                      >
                        <div className="flex items-center mb-4">
                          <div className={`p-3 rounded-xl ${category.bgColor}`}>
                            <Icon className={`w-8 h-8 ${category.iconColor}`} />
                          </div>
                          <h3 className="ml-4 text-xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">
                            {category.name}
                          </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{category.description}</p>
                        <div className="mt-4 flex items-center text-blue-600 font-semibold group-hover:text-blue-700 transition-colors">
                          <span>Voir les questions</span>
                          <ChevronDownIcon className="w-4 h-4 ml-2 rotate-[-90deg] group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Popular Questions */}
          {!searchQuery && (
            <section className="py-16">
              <div className="max-w-4xl mx-auto px-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="text-center mb-12"
                >
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Questions populaires</h2>
                  <p className="text-lg text-gray-600 dark:text-gray-300">
                    Les questions les plus fréquemment posées par nos utilisateurs
                  </p>
                </motion.div>

                <div className="space-y-4">
                  {filteredFAQs.slice(0, 5).map((faq: any, index: number) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 * index }}
                    >
                      <FAQItem question={faq.question} answer={faq.answer} />
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      ) : (
        /* Category Specific Content */
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row gap-12">
              {/* Sidebar */}
              <div className="lg:w-1/3">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  className="sticky top-8"
                >
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Catégories</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setSelectedCategory("fq")}
                      className="flex items-center w-full p-3 text-left rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <QuestionMarkCircleIcon className="w-5 h-5 text-gray-500 mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">Toutes les catégories</span>
                    </button>
                    {categories.map((category) => {
                      const Icon = category.icon;
                      const isSelected = selectedCategory === category.prefix;
                      return (
                        <button
                          key={category.prefix}
                          onClick={() => setSelectedCategory(category.prefix)}
                          className={`flex items-center w-full p-3 text-left rounded-xl transition-all duration-300 ${
                            isSelected
                              ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                              : "hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                        >
                          <Icon className={`w-5 h-5 mr-3 ${isSelected ? "text-white" : "text-gray-500"}`} />
                          <span
                            className={
                              isSelected ? "text-white font-semibold" : "text-gray-700 dark:text-gray-300"
                            }
                          >
                            {category.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="lg:w-2/3">
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {selectedCategoryData && (
                    <div className={`p-6 rounded-2xl mb-8 ${selectedCategoryData.bgColor}`}>
                      <div className="flex items-center mb-4">
                        <selectedCategoryData.icon className={`w-8 h-8 ${selectedCategoryData.iconColor} mr-4`} />
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {selectedCategoryData.name}
                        </h2>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300">{selectedCategoryData.description}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {filteredFAQs && filteredFAQs.length > 0 ? (
                      filteredFAQs.map((faq: any, index: number) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.05 * index }}
                        >
                          <FAQItem question={faq.question} answer={faq.answer} />
                        </motion.div>
                      ))
                    ) : (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
                        <QuestionMarkCircleIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                          Aucune question trouvée
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          Essayez de modifier votre recherche ou contactez notre support.
                        </p>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ChatBubbleLeftRightIcon className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">Vous ne trouvez pas ce que vous cherchez ?</h2>
            <p className="text-xl text-white/90 mb-8">
              Notre équipe support est là pour vous aider 24h/24 et 7j/7
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="/espaces-a-louer-ile-de-france"
                className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Nous contacter
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="mailto:support@eventtobee.com"
                className="px-8 py-4 bg-white/20 backdrop-blur-sm text-white rounded-xl font-semibold border border-white/30 hover:bg-white/30 transition-all duration-300"
              >
                support@eventtobee.com
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ModernHelpCenter;
